import {
  CompareAttribute,
  CompareConfiguration,
  CompareItem,
  CompareOption,
  ComparePlanOption,
  ComparePrice,
  getComparePriceKey,
  normalizeSpecifications
} from '@common-modules/product-catalogue';
import { FormattedProduct } from '../types/productCatalogs.types';
import { ProductSpecificationGroup, SkuVariant } from '../types/product.types';
import { getSimOnlyPlans, isProductHasTag } from './productCatalogHelper';
import { Plan, PlanGroup } from '../types/plan.types';
import {
  COMPARE_SCHEMA_GROUP_TITLES,
  COMPARE_SPEC_BRANDS,
  COMPARE_SPEC_MOCK,
  CompareSpec,
  DEVICE_ONLY_PLAN_LABEL,
  DEVICE_ONLY_PLAN_VALUE,
  DEVICE_ONLY_RRP_PLAN_ID,
  OTHER_BRAND_LABEL,
  PHONE_CATEGORY_TAG,
  PHONE_PLANS_GROUP,
  PHONE_PLANS_HEADER_LABEL,
  SIM_ONLY_PLANS_HEADER_LABEL
} from '../constants/compare-specs';
import formatPrice from './formatPrice';
import slugify from './slugify';
import { setUrlParams } from '@lux/helpers';
import CONSTANTS from '../constants/common';
import CATALOGUE from '../constants/catalogue';
import { trans as t } from '../helpers/localisation';

const getSpecBrandKey = (product: FormattedProduct): string => {
  const source = `${product.brand || ''} ${product.title ||
    product.deviceTitle ||
    ''}`;
  if (/apple|iphone/i.test(source)) {
    return COMPARE_SPEC_BRANDS.APPLE;
  }
  if (/samsung|galaxy/i.test(source)) {
    return COMPARE_SPEC_BRANDS.SAMSUNG;
  }
  return COMPARE_SPEC_BRANDS.DEFAULT;
};

export const getCompareSpecs = (product: FormattedProduct): CompareSpec =>
  COMPARE_SPEC_MOCK[getSpecBrandKey(product)];

const stripHtml = (value?: string | null): string =>
  (value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** AEM-authored specs when available for this device, otherwise the brand-guessed mock. Parses via the same normalizer as the PDP's SpecBox; unlike the PDP's rich-text render, the compare table shows plain text, so markup is stripped here. */
export const getProductAttributes = (
  product: FormattedProduct
): Record<string, string> => {
  const groups = product.specifications;
  if (!groups?.length) {
    return getCompareSpecs(product);
  }
  return normalizeSpecifications(groups).reduce(
    (acc: Record<string, string>, { title, description }) => {
      acc[slugify(title)] = stripHtml(description);
      return acc;
    },
    {}
  );
};

/** Curated (allowlisted) AEM spec keys, in the display order set by COMPARE_SCHEMA_GROUP_TITLES. */
const COMPARE_SCHEMA_ALLOWED_KEYS = COMPARE_SCHEMA_GROUP_TITLES.map(slugify);

/** Curated subset of AEM spec groups actually present across the comparable catalogue, in curated order. */
export const getCompareSchema = (
  products: FormattedProduct[] = []
): CompareAttribute[] => {
  const byKey = new Map<string, CompareAttribute>();
  products
    .filter(isComparableProduct)
    .flatMap(product => product.specifications || [])
    .forEach((group: ProductSpecificationGroup) => {
      const key = slugify(group.groupTitle);
      if (!group.groupTitle || !COMPARE_SCHEMA_ALLOWED_KEYS.includes(key)) {
        return;
      }
      byKey.set(key, { key, label: group.groupTitle });
    });
  return COMPARE_SCHEMA_ALLOWED_KEYS.map(key => byKey.get(key)).filter(
    Boolean
  ) as CompareAttribute[];
};

/** Mirrors the PDP price plan card `sellingPrice`: instalment when present, else upfront. */
const getVariantSellingPrice = (variant: SkuVariant): string =>
  variant.installmentPrice
    ? `${formatPrice(variant.installmentPrice)}/mth`
    : formatPrice(variant.price);

/** Mirrors the PDP `originalPrice`: usual price, only when it beats the selling price. */
const getVariantOriginalPrice = (variant: SkuVariant): string | undefined => {
  const usualPrice = parseFloat(String(variant.usualPrice ?? ''));
  if (!(usualPrice >= 0)) {
    return undefined;
  }
  const sellingValue = Number(variant.installmentPrice ?? variant.price);
  return sellingValue < usualPrice ? formatPrice(usualPrice) : undefined;
};

const getVariantSortValue = (variant: SkuVariant): number =>
  Number(variant.installmentPrice ?? variant.price);

const hasPlanDiscount = (plan: Plan): boolean => {
  const discountedPrice = parseFloat(String(plan.discountedPrice ?? ''));
  const monthlyCharges = parseFloat(String(plan.monthlyCharges ?? ''));
  return discountedPrice >= 0 && discountedPrice < monthlyCharges;
};

/** Mirrors the PDP price plan card `sellingMonthlyCharges`. */
const getPlanSellingMonthlyCharges = (plan: Plan): string => {
  const discountedPrice = parseFloat(String(plan.discountedPrice ?? ''));
  const monthlyCharges = parseFloat(String(plan.monthlyCharges ?? ''));

  if (hasPlanDiscount(plan)) {
    return `${formatPrice(discountedPrice)}/mth`;
  }
  return `${formatPrice(
    discountedPrice > monthlyCharges ? discountedPrice : monthlyCharges
  )}/mth`;
};

/** Mirrors the PDP `originalMonthlyCharges`. */
const getPlanOriginalMonthlyCharges = (plan: Plan): string | undefined =>
  hasPlanDiscount(plan)
    ? `${formatPrice(parseFloat(String(plan.monthlyCharges)))}/mth`
    : undefined;

export const isComparableProduct = (product: FormattedProduct): boolean =>
  Boolean(product?.tags?.length) &&
  isProductHasTag(product, PHONE_CATEGORY_TAG);

/** Plans (of any eligible group) the device actually has SKUs for. */
const getPlansWithDeviceSkus = (
  product: FormattedProduct,
  plans: Plan[]
): Plan[] => {
  const productPlanIds = new Set(
    (product.variants || []).map(({ planId }: SkuVariant) => planId)
  );
  return plans.filter(plan => productPlanIds.has(plan.mecId));
};

/** Phone-plans group only, and only plans the device actually has SKUs for. */
const getPhonePlans = (product: FormattedProduct, plans: Plan[] = []): Plan[] =>
  getPlansWithDeviceSkus(
    product,
    plans.filter(plan => slugify(plan.groupName) === PHONE_PLANS_GROUP)
  );

/** SIM Only Plan IPP group only, and only plans the device actually has SKUs for. */
const getSimOnlyIppPlans = (
  product: FormattedProduct,
  plans: Plan[] = []
): Plan[] => getPlansWithDeviceSkus(product, getSimOnlyPlans(plans));

/** Unlike Phone Plans, SIM Only Plan IPP pricing is pinned to this term instead of the cheapest term available. */
const SIM_ONLY_IPP_TERM = CATALOGUE.INSTALLMENT_MONTHS[0];

/** Cheapest variant per `${size}|${planId}`, mirroring the PDP variant lookup. */
const getCheapestVariants = (
  product: FormattedProduct,
  planIds: Set<string>,
  fixedTermPlanIds: Set<string> = new Set()
): Record<string, SkuVariant> =>
  (product.variants || []).reduce(
    (acc: Record<string, SkuVariant>, variant: SkuVariant) => {
      if (!variant.size || !planIds.has(variant.planId)) {
        return acc;
      }
      if (
        fixedTermPlanIds.has(variant.planId) &&
        variant.monthlyTerm !== SIM_ONLY_IPP_TERM
      ) {
        return acc;
      }
      const key = `${variant.size}|${variant.planId}`;
      const current = acc[key];
      if (
        !current ||
        getVariantSortValue(variant) < getVariantSortValue(current)
      ) {
        acc[key] = variant;
      }
      return acc;
    },
    {}
  );

const getStorageOptions = (
  cheapestVariants: Record<string, SkuVariant>
): CompareOption[] => {
  const cheapestBySize = Object.values(cheapestVariants).reduce(
    (acc: Record<string, SkuVariant>, variant: SkuVariant) => {
      const current = acc[variant.size];
      if (
        !current ||
        getVariantSortValue(variant) < getVariantSortValue(current)
      ) {
        acc[variant.size] = variant;
      }
      return acc;
    },
    {}
  );

  // Cheapest first, so the default (first) option is always price-driven, regardless of stock.
  return Object.values(cheapestBySize)
    .sort((a, b) => getVariantSortValue(a) - getVariantSortValue(b))
    .map(variant => ({
      label: variant.size,
      value: slugify(variant.size),
      price: getVariantSellingPrice(variant)
    }));
};

const toPlanOptions = (plans: Plan[]): CompareOption[] =>
  plans.map(plan => ({
    label: plan.planName,
    value: slugify(plan.planName)
  }));

/** Mirrors the PDP tabs' label source (AEM `groupDisplayName`); only falls back to a static label when AEM has no matching group. */
const getGroupDisplayLabel = (
  planGroups: PlanGroup[],
  groupName: string | undefined,
  fallback: string
): string =>
  planGroups.find(group => group.groupName === groupName)?.groupDisplayName ||
  fallback;

/** Groups the plan dropdown under "Phone Plans" / "SIM Only Plans" headers, in that order. */
const getPlanOptions = (
  phonePlans: Plan[],
  simOnlyPlans: Plan[],
  hasDeviceOnlyOption: boolean,
  planGroups: PlanGroup[] = []
): ComparePlanOption[] => {
  const phonePlansLabel = getGroupDisplayLabel(
    planGroups,
    phonePlans[0]?.groupName,
    PHONE_PLANS_HEADER_LABEL
  );
  const simOnlyPlansLabel = getGroupDisplayLabel(
    planGroups,
    simOnlyPlans[0]?.groupName,
    SIM_ONLY_PLANS_HEADER_LABEL
  );

  const groups: ComparePlanOption[][] = [
    phonePlans.length
      ? [
          { type: 'header', label: phonePlansLabel },
          ...toPlanOptions(phonePlans)
        ]
      : [],
    simOnlyPlans.length
      ? [
          { type: 'header', label: simOnlyPlansLabel },
          ...toPlanOptions(simOnlyPlans)
        ]
      : []
  ].filter(group => group.length);

  return [
    ...groups.flatMap((group, index) =>
      index === 0 ? group : [{ type: 'divider' }, ...group]
    ),
    ...(hasDeviceOnlyOption
      ? [
          { type: 'divider' },
          { label: DEVICE_ONLY_PLAN_LABEL, value: DEVICE_ONLY_PLAN_VALUE }
        ]
      : [])
  ];
};

/** Per business rule, "Device Only" (RRP) pricing only applies when the device has this SKU variant. */
const hasDeviceOnlyRrpVariant = (variants: SkuVariant[] = []): boolean =>
  variants.some(variant => variant.planId === DEVICE_ONLY_RRP_PLAN_ID);

/** PDP deep link — the detail page preselects from these query params. */
const getProductDetailUrl = (
  productUrl: string,
  variant: SkuVariant,
  plan: Plan
): string =>
  setUrlParams(productUrl, {
    [CONSTANTS.URL_PARAMS.PLAN]: slugify(plan.planName),
    [CONSTANTS.URL_PARAMS.SIZE]: slugify(variant.size)
  });

const getPriceMatrix = (
  plans: Plan[],
  cheapestVariants: Record<string, SkuVariant>,
  productUrl: string,
  simOnlyPlanIds: Set<string> = new Set()
): Record<string, ComparePrice> =>
  Object.values(cheapestVariants).reduce(
    (acc: Record<string, ComparePrice>, variant: SkuVariant) => {
      const plan = plans.find(({ mecId }) => mecId === variant.planId);
      if (!plan) {
        return acc;
      }
      acc[getComparePriceKey(slugify(variant.size), slugify(plan.planName))] = {
        devicePrice: getVariantSellingPrice(variant),
        devicePriceStrikethrough: getVariantOriginalPrice(variant),
        devicePriceLiner: simOnlyPlanIds.has(plan.mecId)
          ? t('SIM_ONLY_IPP_LINER')
          : undefined,
        planPrice: getPlanSellingMonthlyCharges(plan),
        planPriceStrikethrough: getPlanOriginalMonthlyCharges(plan),
        href: getProductDetailUrl(productUrl, variant, plan)
      };
      return acc;
    },
    {}
  );

/**
 * Cheapest RRP-priced (planId 8619502_8631722) full-retail price per size, independent of any
 * plan — backs the "Device Only" option.
 */
const getDeviceOnlyPrices = (
  variants: SkuVariant[],
  productUrl: string
): Record<string, ComparePrice> => {
  const rrpVariants = variants.filter(
    variant => variant.planId === DEVICE_ONLY_RRP_PLAN_ID
  );
  const sizes = Array.from(new Set(rrpVariants.map(variant => variant.size)));
  return sizes.reduce((acc: Record<string, ComparePrice>, size) => {
    const cheapest = rrpVariants
      .filter(variant => variant.size === size)
      .reduce((current: SkuVariant | undefined, variant) => {
        return !current || variant.price < current.price ? variant : current;
      }, undefined);
    if (cheapest) {
      acc[getComparePriceKey(slugify(size), DEVICE_ONLY_PLAN_VALUE)] = {
        devicePrice: formatPrice(cheapest.price),
        // Mirrors the RRP catalogue/PDP convention: price is the selling price, usualPrice only shows as a strikethrough when higher.
        devicePriceStrikethrough:
          cheapest.usualPrice > cheapest.price
            ? formatPrice(cheapest.usualPrice)
            : undefined,
        href: setUrlParams(productUrl, {
          [CONSTANTS.URL_PARAMS.SIZE]: slugify(size)
        })
      };
    }
    return acc;
  }, {});
};

const isSelectablePlanOption = (
  option: ComparePlanOption
): option is CompareOption =>
  option.type !== 'header' && option.type !== 'divider';

/** First selectable plan (skips group headers/dividers) — the plan dropdown's default. */
export const getDefaultPlanValue = (item: CompareItem): string | undefined =>
  (item.planOptions || []).find(isSelectablePlanOption)?.value;

/** Whether the device still offers this plan — decides if a carried-over plan stays selected on a device swap. */
export const hasPlanOption = (item: CompareItem, planValue?: string): boolean =>
  Boolean(planValue) &&
  (item.planOptions || []).some(
    (option: ComparePlanOption) =>
      isSelectablePlanOption(option) && option.value === planValue
  );

export const getCompareItemBrand = (item: CompareItem): string =>
  item?.brand || OTHER_BRAND_LABEL;

/** The PDP slug is the last href segment — reused as the compare page's URL identifier. */
export const getCompareItemSlug = (item: CompareItem): string =>
  item?.href
    ?.split('/')
    .filter(Boolean)
    .pop() || '';

/** Rewrites the PDP href to the RRP full-price product page, keeping the same host/slug. */
const getDeviceOnlyHref = (item: CompareItem): string | undefined => {
  const slug = getCompareItemSlug(item);
  const phoneSuffix = `/${CONSTANTS.URL_PATHS.PHONE}/${slug}`;
  if (!slug || !item.href?.endsWith(phoneSuffix)) {
    return undefined;
  }
  const host = item.href.slice(0, -phoneSuffix.length);
  return `${host}/${CONSTANTS.URL_PATHS.RRP_PHONE}/${slug}`;
};

/** "Get" CTA on the compare page — carries whichever dropdowns the user has picked. */
export const getCompareCtaHref = (
  item: CompareItem,
  configuration: CompareConfiguration = {}
): string => {
  const { plan, storage } = configuration;
  if (plan === DEVICE_ONLY_PLAN_VALUE) {
    const deviceOnlyHref = getDeviceOnlyHref(item);
    if (!deviceOnlyHref) {
      return item.href;
    }
    return setUrlParams(deviceOnlyHref, {
      ...(storage && { [CONSTANTS.URL_PARAMS.SIZE]: storage }),
      [CONSTANTS.URL_PARAMS.PAYMENT_TERM]: CATALOGUE.PAYMENT_TYPES.FULL_PRICE
    });
  }
  if (!plan && !storage) {
    return item.href;
  }
  return setUrlParams(item.href, {
    ...(plan && { [CONSTANTS.URL_PARAMS.PLAN]: plan }),
    ...(storage && { [CONSTANTS.URL_PARAMS.SIZE]: storage })
  });
};

export type ToCompareItemOptions = {
  anchorTagPath?: string;
  plans?: Plan[];
  planGroups?: PlanGroup[];
};

export const toCompareItem = (
  product: FormattedProduct,
  options: ToCompareItemOptions = {}
): CompareItem => {
  const { anchorTagPath, plans, planGroups } = options;
  const title = product.deviceTitle || product.title || '';
  const ribbon = product.ribbon;
  const phonePlans = getPhonePlans(product, plans);
  const simOnlyIppPlans = getSimOnlyIppPlans(product, plans);
  const eligiblePlans = [...phonePlans, ...simOnlyIppPlans];
  const cheapestVariants = getCheapestVariants(
    product,
    new Set(eligiblePlans.map(({ mecId }) => mecId)),
    new Set(simOnlyIppPlans.map(({ mecId }) => mecId))
  );
  const productUrl = anchorTagPath ? `${anchorTagPath}/${slugify(title)}` : '#';
  const hasDeviceOnlyOption = hasDeviceOnlyRrpVariant(product.variants);

  return {
    id: String(product.productId || product.id),
    title,
    image: product.heroImage?.src || '',
    price: String(product.customPrice || product.priceFrom || ''),
    href: productUrl,
    badge: Array.isArray(ribbon) ? ribbon[0] : ribbon,
    brand: product.brand,
    tags: product.tags,
    colors: product.hexCodeColors || [],
    storageOptions: getStorageOptions(cheapestVariants),
    planOptions: getPlanOptions(
      phonePlans,
      simOnlyIppPlans,
      hasDeviceOnlyOption,
      planGroups
    ),
    prices: {
      ...getPriceMatrix(
        eligiblePlans,
        cheapestVariants,
        productUrl,
        new Set(simOnlyIppPlans.map(({ mecId }) => mecId))
      ),
      ...(hasDeviceOnlyOption
        ? getDeviceOnlyPrices(product.variants || [], productUrl)
        : {})
    },
    attributes: getProductAttributes(product)
  };
};

export const getCompareCatalog = (
  products: FormattedProduct[] = [],
  options: ToCompareItemOptions = {}
): CompareItem[] =>
  products
    .filter(isComparableProduct)
    .map(product => toCompareItem(product, options));
